import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ReportData } from "../../types/global.d";
import { getAuthToken } from "../../api/auth";
import {
  addReportImagesDirect,
  editReport as apiEditReport,
  editReportImagesDirect,
  deleteReport as apiDeleteReport,
} from "../../api/reports";
import { uploadImagesDirect, getUploadErrorMessage } from "../../api/uploads";
import { getErrorMessage } from "../../utils/api";
import { useDataContext } from "../../contexts/DataContext";
import { useUserContext } from "../../contexts/UserContext";
import { UserStatus, STATIC_URL } from "../../constants";
import LocationPickerModal from "./LocationPickerModal";
import ImageEditor from "./ImageEditor";
import "../styles/EditReportDialog.css";

// Helper function to get full image URL
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return "";

  // If it's already a full URL (blob or http), return as is
  if (
    imagePath.startsWith("blob:") ||
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://")
  ) {
    return imagePath;
  }

  // If it's a relative path, construct the full URL using Google Cloud Storage
  return `${STATIC_URL}${imagePath.replace(/^\//, "")}`;
};

interface EditReportDialogProps {
  report: ReportData;
  onClose: () => void;
  onSuccess: () => void;
}

const getPollutionAreas = (t: any) => [
  { value: 1, label: t("pollution.area100m") },
  { value: 2, label: t("pollution.area500m") },
  { value: 3, label: t("pollution.area1km") },
];

const getActionPlans = (t: any) => [
  { value: 1, label: t("actionPlan.needHelp") },
  { value: 0, label: t("actionPlan.cleanMyself") },
  { value: 2, label: t("actionPlan.groupCleanup") },
];

const getTrashTypes = (t: any) => [
  { value: 0, label: t("trash.domestic"), icon: "domestic.png" },
  { value: 1, label: t("trash.construction"), icon: "construction.png" },
  { value: 2, label: t("trash.hazardous"), icon: "dangerous.png" },
  { value: 3, label: t("trash.liquid"), icon: "liquid.png" },
];

const EditReportDialog: React.FC<EditReportDialogProps> = ({
  report,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { updateData, loadReportDetails } = useDataContext();
  const { user } = useUserContext();
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.status === UserStatus.admin;
  const isOwner = user?.nickname === report.user?.nickname;
  const canEditImages = isAdmin || (isOwner && !report.verified);

  // Form state
  const [comment, setComment] = useState(report.comment || "");
  const locationName = report.locationName || "";
  const [geoposition, setGeoposition] = useState(report.geoposition);
  const [pollutionLevel, setPollutionLevel] = useState(report.amount || 1);
  const [pollutionArea, setPollutionArea] = useState(report.area || 1);
  const [actionPlan, setActionPlan] = useState(
    report.action !== undefined ? report.action : 1
  );
  const [trashTypes, setTrashTypes] = useState<number[]>(
    report.trashTypes || []
  );
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Image editing state
  const [images, setImages] = useState<string[]>(
    report.images?.map(getImageUrl) || []
  );
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);

  const handleTrashTypeToggle = (type: number) => {
    setTrashTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleImagesChange = useCallback(
    (newImages: string[], newFiles: File[]) => {
      setImages(newImages);
      setNewImageFiles(newFiles);
    },
    []
  );

  const handleLocationSelected = useCallback(
    (location: { latitude: number; longitude: number }) => {
      setGeoposition({
        latitude: location.latitude,
        longitude: location.longitude,
      });
      // Keep the existing location name since LocationPickerModal doesn't provide address
      // User can manually edit if needed
      setShowLocationPicker(false);
    },
    []
  );

  const isDataChanged = useCallback(() => {
    const originalImageUrls = report.images?.map(getImageUrl) || [];
    const imagesChanged =
      JSON.stringify(images) !== JSON.stringify(originalImageUrls);

    return (
      comment !== (report.comment || "") ||
      locationName !== (report.locationName || "") ||
      geoposition.latitude !== report.geoposition.latitude ||
      geoposition.longitude !== report.geoposition.longitude ||
      pollutionLevel !== (report.amount || 1) ||
      pollutionArea !== (report.area || 1) ||
      actionPlan !== (report.action !== undefined ? report.action : 1) ||
      JSON.stringify(trashTypes) !== JSON.stringify(report.trashTypes || []) ||
      imagesChanged
    );
  }, [
    comment,
    locationName,
    geoposition,
    pollutionLevel,
    pollutionArea,
    actionPlan,
    trashTypes,
    images,
    report,
  ]);

  const handleDelete = async () => {
    if (deleting || loading) return;
    if (!window.confirm(t("report.deleteConfirm"))) return;

    const token = getAuthToken();
    if (!token) {
      alert(t("auth.authRequired"));
      return;
    }

    setDeleting(true);
    try {
      await apiDeleteReport(report._id, token);
      await updateData();
      alert(t("report.reportDeleted"));
      onSuccess();
    } catch (error) {
      alert(`${t("errors.failedToDeleteReport")}: ${getErrorMessage(error)}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (loading || !isDataChanged()) return;

    const token = getAuthToken();
    if (!token) {
      alert(t("auth.authRequired"));
      return;
    }

    if (images.length === 0) {
      alert(t("report.atLeastOneImage"));
      return;
    }

    setLoading(true);
    try {
      // Check if data changed (non-image fields)
      const dataChanged =
        comment !== (report.comment || "") ||
        locationName !== (report.locationName || "") ||
        geoposition.latitude !== report.geoposition.latitude ||
        geoposition.longitude !== report.geoposition.longitude ||
        pollutionLevel !== (report.amount || 1) ||
        pollutionArea !== (report.area || 1) ||
        actionPlan !== (report.action !== undefined ? report.action : 1) ||
        JSON.stringify(trashTypes) !== JSON.stringify(report.trashTypes || []);

      // Update report data if changed
      if (dataChanged) {
        await apiEditReport(
          {
            _id: report._id,
            comment,
            locationName,
            geoposition: {
              latitude: geoposition.latitude,
              longitude: geoposition.longitude,
            },
            area: pollutionArea,
            amount: pollutionLevel,
            trashTypes,
            action: actionPlan,
          },
          token
        );
      }

      // Check if images changed
      const originalImageUrls = report.images?.map(getImageUrl) || [];
      const imagesChanged =
        JSON.stringify(images) !== JSON.stringify(originalImageUrls);

      if (imagesChanged) {
        const currentExistingImages = images.filter(
          (image) => !image.startsWith("blob:")
        );
        const existingImagesUnchanged =
          JSON.stringify(currentExistingImages) ===
          JSON.stringify(originalImageUrls);

        if (newImageFiles.length > 0) {
          // New images go straight to GCS; submit only their keys.
          const imageKeys = await uploadImagesDirect(newImageFiles, token);
          if (existingImagesUnchanged) {
            await addReportImagesDirect({ _id: report._id, imageKeys }, token);
          } else {
            await editReportImagesDirect({ _id: report._id, imageKeys }, token);
          }
        } else if (!existingImagesUnchanged) {
          throw new Error(t("errors.failedToUploadImages"));
        }
      }

      await updateData();
      // Status sync only overlays status/sortTimestamp; force-refresh this
      // report's full data so edited fields/coordinates propagate.
      await loadReportDetails(report._id, true);
      alert(t("report.reportUpdated"));
      onSuccess();
    } catch (error) {
      alert(
        `${t("errors.failedToUpdateReport")}: ${getUploadErrorMessage(error, t)}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const POLLUTION_AREAS = getPollutionAreas(t);
  const ACTION_PLANS = getActionPlans(t);
  const TRASH_TYPES = getTrashTypes(t);

  return (
    <>
      <div className="edit-dialog-overlay" onClick={onClose} />
      <div className="edit-dialog">
        <div className="edit-dialog-header">
          <h2>{t("report.editReport")}</h2>
          <button className="edit-dialog-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="edit-dialog-content">
          {/* Images Section */}
          {canEditImages ? (
            <div className="edit-section">
              <label className="edit-label">{t("report.images")}</label>
              <ImageEditor
                images={images}
                onImagesChange={handleImagesChange}
                disabled={loading || deleting}
                minImages={1}
              />
            </div>
          ) : (
            <div className="edit-section">
              <label className="edit-label">{t("report.imagesReadonly")}</label>
              <div className="images-readonly">
                {images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${t("report.report")} ${index + 1}`}
                    className="readonly-image"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Location Section */}
          <div className="edit-section">
            <label className="edit-label">{t("report.location")}</label>
            <div className="location-display">
              <div className="location-text">
                <div className="location-name">{locationName}</div>
                <div className="location-coords">
                  {geoposition.latitude.toFixed(6)},{" "}
                  {geoposition.longitude.toFixed(6)}
                </div>
              </div>
              <button
                className="change-location-btn"
                onClick={() => setShowLocationPicker(true)}
              >
                {t("report.changeLocation")}
              </button>
            </div>
          </div>

          {/* Comment Section */}
          <div className="edit-section">
            <label className="edit-label">{t("report.comment")}</label>
            <textarea
              className="edit-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("report.commentPlaceholder")}
              rows={3}
            />
          </div>

          {/* Action Plan Section */}
          <div className="edit-section">
            <label className="edit-label">{t("actionPlan.actionPlan")}</label>
            <div className="options-grid">
              {ACTION_PLANS.map((plan) => (
                <button
                  key={plan.value}
                  className={`option-btn ${
                    actionPlan === plan.value ? "active" : ""
                  }`}
                  onClick={() => setActionPlan(plan.value)}
                >
                  {plan.label}
                </button>
              ))}
            </div>
          </div>

          {/* Pollution Level Section */}
          <div className="edit-section">
            <label className="edit-label">
              {t("pollution.pollutionLevel")}
              <div className="amount-display">
                <span className="amount-value">{pollutionLevel}</span>
                <img
                  src={`${process.env.PUBLIC_URL}/images/trash${
                    pollutionLevel < 10 ? 0 : pollutionLevel < 20 ? 1 : 2
                  }.png`}
                  alt={`Level ${pollutionLevel}`}
                  className="pollution-level-icon"
                />
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="40"
              value={pollutionLevel}
              onChange={(e) => setPollutionLevel(Number(e.target.value))}
              className="pollution-slider"
            />
          </div>

          {/* Pollution Area Section */}
          <div className="edit-section">
            <label className="edit-label">{t("pollution.pollutionArea")}</label>
            <div className="options-grid">
              {POLLUTION_AREAS.map((area) => (
                <button
                  key={area.value}
                  className={`option-btn ${
                    pollutionArea === area.value ? "active" : ""
                  }`}
                  onClick={() => setPollutionArea(area.value)}
                >
                  {area.label}
                </button>
              ))}
            </div>
          </div>

          {/* Trash Types Section */}
          <div className="edit-section">
            <label className="edit-label">{t("trash.trashTypes")}</label>
            <div className="trash-types-grid">
              {TRASH_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`trash-type-btn ${
                    trashTypes.includes(type.value) ? "active" : ""
                  }`}
                  onClick={() => handleTrashTypeToggle(type.value)}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/${type.icon}`}
                    alt={type.label}
                    className="trash-type-icon"
                  />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="edit-dialog-footer">
          <div className="footer-left">
            {!report.cleaned && (
              <button
                className="edit-delete-btn"
                onClick={handleDelete}
                disabled={loading || deleting}
              >
                {deleting
                  ? t("report.deletingReport")
                  : t("report.deleteReport")}
              </button>
            )}
          </div>
          <div className="footer-right">
            <button className="edit-cancel-btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              className="edit-submit-btn"
              onClick={handleSubmit}
              disabled={loading || deleting || !isDataChanged()}
            >
              {loading ? t("report.saving") : t("report.saveChanges")}
            </button>
          </div>
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPickerModal
          onLocationSelected={handleLocationSelected}
          onClose={() => setShowLocationPicker(false)}
          initialLocation={geoposition}
        />
      )}
    </>
  );
};

export default EditReportDialog;
